import React from 'react';
import styled from 'styled-components';
import H2 from 'components/H2';
import H4 from 'components/H4';
import P from 'components/P';
import PropTypes from 'prop-types';
import Icon from 'components/Icon';

// Styled components
const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 24px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 1200px) {
    padding: 32px 48px;
    margin-left: -25px; /* Offset to adjust for sidebar space */
  }
`;

const PageHeader = styled.div`
  margin-bottom: 40px;
  font-family: 'Inter';
  text-align: left;
`;

const PageTitle = styled(H2)`
  font-size: 36px;
  margin-bottom: 12px;
  font-weight: 800;
  font-family: 'Inter';
  background: linear-gradient(
    90deg,
    ${props => props.theme.primary || '#5db7de'} 0%,
    ${props => props.theme.secondary || '#3f8eb5'} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.025em;
`;

const PageDescription = styled(P)`
  color: ${props => props.theme.gray};
  font-size: 18px;
  text-align: left;
  line-height: 1.5;
  margin: 0 auto;
`;

const Card = styled.div`
  background-color: ${props => props.theme.white};
  border-radius: 12px;
  margin-bottom: 32px;
  padding: 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
`;

const CardContent = styled.div`
  margin-top: 24px;
`;

const VersionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const VersionNumber = styled(H2)`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.black};
  margin: 0;
`;

const VersionDate = styled(P)`
  color: ${props => props.theme.gray};
  font-size: 14px;
  font-weight: 500;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 20px;
`;

const ChangeEntry = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const ChangeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  h4 {
    font-weight: 600;
    font-size: 18px;
    color: ${props => props.theme.black};
    margin: 0;
  }
`;

const ChangeDescription = styled(P)`
  margin-left: 0;
  color: ${props => props.theme.gray};
  font-size: 15px;
  line-height: 1.6;
  margin-top: 12px;
  margin-bottom: 0;
`;

const ChangeImage = styled.div`
  margin: 0 0 16px 0;

  img {
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.06);
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.01);
    }
  }

  .caption {
    font-size: 14px;
    color: ${props => props.theme.gray};
    margin-top: 8px;
    font-style: italic;
    text-align: center;
  }
`;

const Separator = styled.hr`
  border: 0;
  height: 1px;
  background-color: rgba(0, 0, 0, 0.06);
  margin: 24px 0;
  width: 100%;
  display: block;
  clear: both;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background-color: ${props => props.backgroundColor};
  color: ${props => props.textColor};
  box-shadow: 0 2px 5px ${props => `${props.textColor}15`};
  transition: transform 0.1s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  margin: 32px 0 20px 0;

  &:first-child {
    margin-top: 0;
  }
`;

const SectionContent = styled.div`
  margin-left: 0;
`;

const TypeSection = styled.div`
  margin-bottom: 40px;

  &:last-child {
    margin-bottom: 0;
  }
`;

// Types for component
const ChangeTypeBadge = ({ type }) => {
  switch (type) {
    case 'feature':
      return (
        <Badge backgroundColor="#ebf9f1" textColor="#0f7832">
          <Icon name="bolt" fontSize={12} color="#0f7832" customStyle={{ marginRight: '6px' }} />
          Features
        </Badge>
      );
    case 'improvement':
      return (
        <Badge backgroundColor="#eaf1ff" textColor="#0050b3">
          <Icon name="fire" fontSize={12} color="#0050b3" customStyle={{ marginRight: '6px' }} />
          Improvements
        </Badge>
      );
    case 'bugfix':
      return (
        <Badge backgroundColor="#fff9eb" textColor="#8a6500">
          <Icon name="bug" fontSize={12} color="#8a6500" customStyle={{ marginRight: '6px' }} />
          Bug Fixes
        </Badge>
      );
    case 'known-issue':
      return (
        <Badge backgroundColor="#ffebeb" textColor="#a30000">
          <Icon
            name="exclamation"
            fontSize={12}
            color="#a30000"
            customStyle={{ marginRight: '6px' }}
          />
          Known Issues
        </Badge>
      );
    default:
      return null;
  }
};

ChangeTypeBadge.propTypes = {
  type: PropTypes.oneOf(['feature', 'improvement', 'bugfix', 'known-issue']).isRequired,
};

// Sample changelog data
const changelogData = [
  {
    number: 'v3.0.0',
    date: 'June 11, 2026',
    changes: [
      {
        type: 'feature',
        title: 'Post-Install Health Checks',
        description:
          'We are excited to launch Health Checks! After a device is installed, Aquarian can run a health check to validate flow, pressure, and connectivity so you know each location was set up correctly before leaving the site. Health checks can be triggered automatically on installation or run on demand, and a dedicated dashboard lets you review results, see who and what triggered each check, and filter to find exactly what you are looking for.',
      },
      {
        type: 'improvement',
        title: 'Health Check experience refinements',
        description:
          'We have polished the Health Check dashboard and detail views based on your feedback, making it easier to scan results, understand what was checked, and dig into the details of any individual health check.',
      },
      {
        type: 'improvement',
        title: 'Improved water filter pressure check logic',
        description:
          'Refined the pressure-check logic for water filter locations so post-install health checks and pressure readings are evaluated more accurately.',
      },
      {
        type: 'improvement',
        title: 'More filter options for alerts',
        description:
          'We have expanded the keys you can filter alerts by, giving you more control over how you slice and review the alerts that matter most to you.',
      },
      {
        type: 'bugfix',
        title: 'Health check reliability fixes',
        description:
          'Resolved a number of issues across the health check experience, including the reported status, the attempts column, the post-install pressure test, how the most recent health check is displayed, and assorted detail-view problems, so results are accurate and easy to read.',
      },
      {
        type: 'bugfix',
        title: 'Corrected RO wastewater percentage calculation',
        description:
          'Fixed an issue where the wastewater percentage shown on RO locations could be calculated incorrectly. The figure now accurately reflects the ratio of water sent to drain.',
      },
      {
        type: 'bugfix',
        title: 'Location detail view fixes',
        description:
          'Resolved several small issues in the location detail view so information loads and displays reliably.',
      },
    ],
  },
  {
    number: 'v2.9.0',
    date: 'May 26, 2026',
    changes: [
      {
        type: 'feature',
        title: 'Location Status',
        description:
          'We have introduced a more comprehensive Location Status that gives you an at-a-glance read on the health and standing of each location. Status is surfaced across the dashboard so you can quickly spot which locations need attention.',
      },
      {
        type: 'improvement',
        title: 'Faster, more reliable data exports',
        description:
          'Large location data exports are now processed asynchronously in chunks. This prevents timeouts and keeps exports reliable even for accounts with a very large number of locations.',
      },
      {
        type: 'improvement',
        title: 'Admin-only access controls',
        description:
          'We have tightened access controls so that sensitive actions, including the location "Export Data" menu, are restricted to admin users. This keeps account data better protected while still giving everyday users the tools they need.',
      },
    ],
  },
  {
    number: 'v2.8.0',
    date: 'April 20, 2026',
    changes: [
      {
        type: 'feature',
        title: 'Brand metrics dashboard',
        description:
          'The Brands page now includes a metrics dashboard showing installed, pending, and active alert counts at a glance. This makes it much easier to understand the overall health of a brand and where attention is needed across its locations.',
      },
      {
        type: 'improvement',
        title: 'Smoother session handling',
        description:
          'Improved how the app handles expired sessions so you are guided cleanly to sign back in rather than running into unexpected errors.',
      },
      {
        type: 'bugfix',
        title: 'Dropdown overflow fix',
        description:
          'Fixed a dropdown that could overflow the table layout, keeping the location list clean and readable.',
      },
    ],
  },
  {
    number: 'v2.7.0',
    date: 'March 18, 2026',
    changes: [
      {
        type: 'feature',
        title: 'Disconnect alert status column',
        description:
          'Added a status column dedicated to disconnect alerts so you can quickly identify locations that have lost connectivity, separate from other alert types.',
      },
      {
        type: 'bugfix',
        title: 'Assorted UI and dropdown fixes',
        description:
          'Resolved a batch of UI issues across the dashboard, including dropdown and select behavior, for a more consistent and reliable experience.',
      },
    ],
  },
  {
    number: 'v2.6.0',
    date: 'February 21, 2026',
    changes: [
      {
        type: 'feature',
        title: 'Location Importer with Smartsheet integration',
        description:
          'You can now onboard locations in bulk using the Location Importer, complete with a Smartsheet webhook integration. This dramatically speeds up bringing large batches of new locations into Aquarian.',
      },
      {
        type: 'improvement',
        title: 'More accurate disconnect alerting',
        description:
          'Disconnect alerts now use a dedicated lookback window, separate from regular alerts, so connectivity issues are reflected more accurately in location status and the alerts list.',
      },
      {
        type: 'improvement',
        title: 'More reliable alert processing',
        description:
          'Improved the reliability of the background jobs that create and send alerts, along with richer logging behind the scenes, so alerts are generated and delivered more consistently.',
      },
    ],
  },
  {
    number: 'v2.5.0',
    date: 'January 27, 2026',
    changes: [
      {
        type: 'feature',
        title: 'Search locations by store number',
        description:
          'You can now search the location list by store number, making it much faster to find a specific location for retail and multi-site accounts.',
      },
      {
        type: 'improvement',
        title: 'Improved RO filter capacities',
        description:
          'Reworked the RO filter capacities module so cartridge life and capacity information is clearer and more accurate.',
      },
      {
        type: 'improvement',
        title: 'Refined alert thresholds',
        description:
          'Corrected the bypass alert threshold to 40 gallons and improved low inlet pressure alerting for RO locations, so the alerts you receive better reflect real conditions in the field.',
      },
      {
        type: 'bugfix',
        title: 'Fixed over-generation of alerts',
        description:
          'Resolved a bug that could cause a large number of alerts to be generated unexpectedly. Alert creation now behaves as intended.',
      },
      {
        type: 'bugfix',
        title: 'Alerts modal and filter fixes',
        description:
          'Fixed alerts not displaying correctly in the modal and corrected a filter that incorrectly flagged filters installed more than 52 weeks ago as needing replacement.',
      },
    ],
  },
  {
    number: 'v2.4.0',
    date: 'December 17, 2025',
    changes: [
      {
        type: 'bugfix',
        title: 'Accurate alert counts',
        description:
          'Fixed the sidebar alert count and pending alert badge so the numbers you see always match the actual number of active alerts.',
      },
      {
        type: 'bugfix',
        title: 'Disconnected RO no longer defaults to bypass',
        description:
          'Resolved an issue where a disconnected RO location could incorrectly display its bypass as on. Bypass state is now reported accurately even when a device loses connectivity.',
      },
      {
        type: 'bugfix',
        title: 'Filter capacity display fix',
        description:
          'Corrected an error in how filter capacities were displayed for certain configurations.',
      },
      {
        type: 'bugfix',
        title: 'Configuration redirect fix',
        description:
          'Fixed a redirect issue that could occur when navigating to a location configuration.',
      },
    ],
  },
  {
    number: 'v2.3.0',
    date: 'September 17, 2025',
    changes: [
      {
        type: 'improvement',
        title: 'Smarter default sorting for the location list',
        description:
          'The location list now defaults to showing your most recently installed locations first, making it easier to keep track of new installs.',
      },
      {
        type: 'improvement',
        title: 'Updated contact information',
        description:
          'Updated the footer with the correct contact information so it is easy to reach the right team when you need support.',
      },
      {
        type: 'bugfix',
        title: 'RO and bypass alert fixes',
        description:
          'Resolved several issues with RO and bypass alerts so the alerts surfaced in the dashboard more accurately reflect device behavior.',
      },
      {
        type: 'bugfix',
        title: 'Editing and blank view fixes',
        description:
          'Fixed a blank screen that could appear when editing locations, and improved saving so only the fields you actually change are submitted.',
      },
    ],
  },
  {
    number: 'v2.2.0',
    date: 'April 17, 2025',
    changes: [
      {
        type: 'feature',
        title: 'Changelog is here!',
        description:
          'Our development pace has increased rapidly since the start of 2025. To help keep our customers and internal users up to date with the latest changes, we are introducing a changelog. This will be updated with each release and will be accessible from the settings menu in the Aquarian Dashboard.',
      },
      {
        type: 'feature',
        title: 'Breadcrumb navigation bar and updated location detail view header',
        description:
          'Breadcrumb navigation makes it easier to keep up with what location you are on and what brand/account it is tied to. Clicking the location ID in the breadcrumb also allows you to quickly switch between devices at a given address. Additionally, the location detail view header has been updated to be more uniform and easier to read.',
        image: {
          src: 'https://kinetico.s3.us-east-1.amazonaws.com/changelog-images/breadcrumb-v2.2.0.gif',
          caption: 'New breadcrumb navigation showing location hierarchy',
        },
      },
      {
        type: 'feature',
        title: 'Added "Open in AWS" button for KineticoPRO Admins',
        description:
          'Added an "Open in AWS" button in the Location Detail View for easier access to AWS resources. This endables easier troubleshooting and debugging of problems with a location. This button can be found in the "..." button in the top right of the location detail screen.',
      },
      {
        type: 'feature',
        title: 'Location Info Drawer',
        description:
          'We have moved most of the location info to a neat and easy to access drawer. You can access this by clicking the "i" icon in the top right corner of the location detail screen. Here you will find the location details, contact info, device status, and configuration details.',
        image: {
          src:
            'https://kinetico.s3.us-east-1.amazonaws.com/changelog-images/Screenshot+2025-04-04+at+3.47.17%E2%80%AFPM.png',
          caption: 'New location info drawer',
        },
      },
      {
        type: 'improvement',
        title: 'Alerts list is now clickable!',
        description:
          'When viewing the alerts list, you can now click on a row to jump directly to the location detail view associated with that alert.',
      },
      {
        type: 'improvement',
        title: 'Quick Stats Component',
        description:
          'New component implemented on RO and WF pages to replace individual stat modules for improved consistency. The quick stats display at the top of the page for RO locations to bring pressure information into focus first.',
      },
      {
        type: 'feature',
        title: 'Auto-fill for location details',
        description:
          'Added auto-fill functionality for city and state when zip code is entered on the details screen.',
      },
      {
        type: 'improvement',
        title: 'Dashboard UI and UX overhaul',
        description:
          'Complete redesign of the dashboard interface for better usability and visual appeal. This will enable a better understanding of the data as well as a better user experience for customers and internal users by providing a uniform design language to be used between all three location detail view types. While the main focus for this release was on water filter and RO locations, an update for the location detail view of softener locations is coming soon.',
      },
      {
        type: 'improvement',
        title: 'Location Detail View restructure',
        description:
          'Redesigned to be more uniform between location types, with device information moved to a side sheet and modules reorganized for better readability.',
      },
      {
        type: 'improvement',
        title: 'Sidebar redesign',
        description:
          'Reworked Sidebar UI to fit our modern design language and have a more uniform layout. Added icons to easily identify each entry in the sidebar.',
      },
      {
        type: 'improvement',
        title: 'Updated typography',
        description:
          'The global font for our dashboard has been updated to Inter which is a modern and clean font that is easy to read and provides a consistent look and feel across the application.',
      },
      {
        type: 'improvement',
        title: 'RO Pages layout enhancements',
        description:
          'Adjusted layout, updated Flow Volume Module and RO Filter Cartridges module styling, and enhanced Bypass model functionality.',
      },
      {
        type: 'improvement',
        title: 'Water Filter Pages layout enhancements',
        description:
          'Updated Flow Volume Module and Filter Capacities module with improved styling and layout across different configurations.',
      },
      {
        type: 'improvement',
        title: 'Adjusted filter capacities module logic for sediment only locations',
        description:
          ' Previously, if a location was configured as a sediment only location, the filter capacities would show x/0 for the flow capacity. This has been updated to explain sediment filters do not have flow capacity.',
      },
      {
        type: 'improvement',
        title: 'Location Creation UI/UX updates',
        description:
          'Improved layout for each screen in the location creation process. Moved next and previous buttons to a static bottom bar. Updated device configuration step to improve clarity and ease of use.',
      },
      {
        type: 'bugfix',
        title: 'Bypass flow graph updated.',
        description:
          'The bypass flow graph has been updated to only show data if the flow for a given day was greater than 1 gallon.',
      },
      {
        type: 'bugfix',
        title: 'Removed bypass module from softener pages',
        description:
          'Removed bypass module since it does not apply to softeners and improved overall layout.',
      },
    ],
  },
  {
    number: 'v2.1.0',
    date: 'March 15, 2025',
    changes: [
      {
        type: 'feature',
        title: 'Location Status Column',
        description:
          'We have added a new column to the location detail view to show the status of a location. This will show a green checkmark if the location is in good standing, a yellow ! if the location has had an alert within the last 48 hours, and a gray X if the location has not been setup yet. Hovering the status icon for a location will give you more info about the status. We will update the logic behind this feature to be more comprehensive in a future update. ',
      },
      {
        type: 'feature',
        title: 'Filter by location installationstatus',
        description:
          'You can now filter the location list by installation status. This will allow you to show only locations that are installed or not installed.',
      },
      {
        type: 'improvement',
        title: 'Ability to specify a previous date when marking an RO membrane as changed.',
        description:
          'This will allow for spcifying of a previous date when marking an RO membrane as changed. This is useful for locations that have had the membrane replaced but the customer did not know to mark the membrane as replaced.',
      },
      {
        type: 'improvement',
        title: 'Added a log of membrane changes to RO locations',
        description:
          'When marking a membrane as changed, you will now see a log of all previous membrane changes as well as the user who marked the change. For any changes that happened before this feature was implemented, "Unknown" will appear for the user since we were not previously tracking this information.',
      },
      {
        type: 'bugfix',
        title: 'Removed "Mark as viewed" button',
        description:
          'We have removed the "Mark as viewed" button for alerts. This was a source of confusion for customers since marking an alert as viewed would simply delete the alert entirely. We will revisit this feature in the future with a more intuitive solution.',
      },
    ],
  },
];

const Changelog = () => (
  <Container>
    <PageHeader>
      <PageTitle>Changelog</PageTitle>
      <PageDescription>Updates and improvements to KineticoPRO Aquarian</PageDescription>
    </PageHeader>

    {changelogData.map(version => {
      // Group changes by type
      const changesByType = version.changes.reduce((acc, change) => {
        if (!acc[change.type]) {
          acc[change.type] = [];
        }
        acc[change.type].push(change);
        return acc;
      }, {});

      // Order of change types
      const typeOrder = ['feature', 'improvement', 'bugfix', 'known-issue'];

      return (
        <Card key={version.number}>
          <CardHeader>
            <VersionHeader>
              <VersionNumber>{version.number}</VersionNumber>
              <VersionDate>{version.date}</VersionDate>
            </VersionHeader>
          </CardHeader>
          <CardContent>
            {typeOrder.map(type => {
              if (!changesByType[type] || changesByType[type].length === 0) return null;

              return (
                <TypeSection key={`${version.number}-${type}`}>
                  <SectionTitle>
                    <ChangeTypeBadge type={type} />
                  </SectionTitle>
                  <SectionContent>
                    {changesByType[type].map((change, changeIndex) => (
                      <React.Fragment key={`${version.number}-${type}-${changeIndex}`}>
                        <ChangeEntry>
                          <ChangeHeader>
                            <H4>{change.title}</H4>
                          </ChangeHeader>
                          {change.image && (
                            <ChangeImage>
                              <img
                                src={change.image.src}
                                alt={change.image.caption || change.title}
                              />
                              {change.image.caption && (
                                <div className="caption">{change.image.caption}</div>
                              )}
                            </ChangeImage>
                          )}
                          <ChangeDescription>{change.description}</ChangeDescription>
                        </ChangeEntry>
                        {changeIndex < changesByType[type].length - 1 && <Separator />}
                      </React.Fragment>
                    ))}
                  </SectionContent>
                </TypeSection>
              );
            })}
          </CardContent>
        </Card>
      );
    })}
  </Container>
);

export default Changelog;
